import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Eye, Save } from "lucide-react";
import { Slide, WorkspaceConfig, DEFAULT_SETTINGS } from "@shared/types";
import { api, ApiError } from "@/lib/api";
import { findIncompleteSlides } from "@/lib/slide-factory";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { SlideListEditor, sharedGroupUploader } from "@/components/admin/SlideListEditor";
import { PreviewOverlay } from "@/components/admin/PreviewOverlay";

export default function SharedGroupEditorPage() {
  const { id = "" } = useParams();
  const [name, setName] = useState("");
  const [slides, setSlides] = useState<Slide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .getSharedGroup(id)
      .then(({ group }) => {
        if (cancelled) return;
        setName(group.name);
        setSlides(group.slides);
      })
      .catch((err) => !cancelled && setError(err instanceof ApiError ? err.message : "Failed to load"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Always-current slides so save() can tell if edits arrived mid-request.
  const slidesRef = useRef(slides);
  useEffect(() => {
    slidesRef.current = slides;
  }, [slides]);

  // Don't let a tab close / refresh silently discard unsaved edits.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  async function save() {
    // The server drops slides that fail normalization — warn instead of losing work.
    const incomplete = findIncompleteSlides(slides, DEFAULT_SETTINGS.defaultDurationSec);
    if (incomplete.length > 0) {
      setError(
        `Not saved — ${incomplete.length === 1 ? "one slide is" : `${incomplete.length} slides are`} ` +
          `incomplete and would be deleted: ${incomplete.join(", ")}. Fill in the missing info or remove ` +
          `the slide, then save again.`,
      );
      return;
    }

    setSaving(true);
    setError(null);
    const snapshot = slides;
    try {
      const { group } = await api.saveSharedGroup(id, name, snapshot);
      if (slidesRef.current === snapshot) {
        setSlides(group.slides);
        setDirty(false);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="container py-12 text-muted-foreground">Loading…</div>;
  if (error && slides.length === 0 && !name)
    return (
      <div className="container py-12">
        <p className="text-destructive">{error}</p>
        <Button asChild variant="link" className="px-0">
          <Link to="/admin/shared-groups">Back to shared groups</Link>
        </Button>
      </div>
    );

  const previewConfig: WorkspaceConfig = {
    settings: { ...DEFAULT_SETTINGS },
    slides,
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <header className="sticky top-0 z-20 border-b bg-background/95 backdrop-blur">
        <div className="container flex h-16 items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="icon" aria-label="Back">
              <Link
                to="/admin/shared-groups"
                onClick={(e) => {
                  if (dirty && !window.confirm("You have unsaved changes. Leave without saving?")) {
                    e.preventDefault();
                  }
                }}
              >
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex items-center gap-2 font-semibold">
              Shared group
              {dirty && <Badge variant="secondary">Unsaved</Badge>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPreview(true)}>
              <Eye className="mr-1 h-4 w-4" /> Preview
            </Button>
            <Button size="sm" onClick={() => void save()} disabled={!dirty || saving}>
              <Save className="mr-1 h-4 w-4" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </header>

      <main className="container space-y-6 py-6">
        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="max-w-md space-y-2">
          <Label htmlFor="group-name">Group name</Label>
          <Input
            id="group-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setDirty(true);
            }}
          />
        </div>

        <SlideListEditor
          slides={slides}
          onSlidesChange={(next) => {
            setSlides(next);
            setDirty(true);
          }}
          uploadMedia={sharedGroupUploader(id)}
          defaultDuration={DEFAULT_SETTINGS.defaultDurationSec}
        />
      </main>

      {preview && <PreviewOverlay config={previewConfig} onClose={() => setPreview(false)} />}
    </div>
  );
}
